// MaterialListTable.tsx — Presentational table for per-panel materials.
// Extracted from MaterialsListDialog so the import-preview path (CV-180) can
// reuse the same row layout without duplicating column definitions.

import { calcMaterials, type MaterialsResult } from '../utils/materialsCalc';
import { formatImperial } from '../utils/imperial';
import { formatNumberOrDash, EM_DASH } from '../utils/format';
import type { PanelData } from '../types/panelvar';

export interface MaterialListTableProps {
  panels: PanelData[];
  isMetric: boolean;
  /** Optional highlight — the row matching this panel id gets `.active-row`. */
  activePanelId?: string | null;
}

export default function MaterialListTable({ panels, isMetric, activePanelId }: MaterialListTableProps) {
  const results: MaterialsResult[] = panels.map(p => calcMaterials(p));

  const fmtVol  = (r: MaterialsResult) => isMetric ? formatNumberOrDash(r.concreteCuM, 'm³', 3) : formatNumberOrDash(r.concreteCuYd, 'yd³', 3);
  const fmtWt   = (r: MaterialsResult) => {
    if (isMetric) {
      return Number.isFinite(r.panelWeightKg) ? `${Math.round(r.panelWeightKg).toLocaleString()} kg` : EM_DASH;
    }
    return formatNumberOrDash(r.panelWeightKips, 'kips', 1);
  };
  const fmtArea = (sqFt: number, sqM: number) => isMetric ? formatNumberOrDash(sqM, 'm²', 1) : formatNumberOrDash(sqFt, 'sf', 1);
  const fmtLoad = (lb: number) => {
    // 0 means "no pick/brace points" — render as dash by design, same as missing.
    if (lb === 0 || !Number.isFinite(lb)) return EM_DASH;
    return isMetric ? `${Math.round(lb * 0.453592).toLocaleString()} kg` : `${(lb / 1000).toFixed(1)} k`;
  };
  const fmtCg = (inches: number) => {
    if (!Number.isFinite(inches)) return EM_DASH;
    return isMetric ? `${Math.round(inches * 25.4)} mm` : formatImperial(inches);
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="cv-table" style={{ fontSize: 11 }}>
        <thead>
          <tr>
            <th>Mark</th>
            <th>Vol</th>
            <th>Weight</th>
            <th>CG X</th>
            <th>CG Y</th>
            <th>Fin. Area</th>
            <th>H/T</th>
            <th>Ld/Pick</th>
            <th>Ld/Brace</th>
            <th>Br</th>
            <th>Pk</th>
            <th>Wc</th>
          </tr>
        </thead>
        <tbody>
          {panels.map((p, i) => {
            const r = results[i];
            return (
              <tr key={p.id || r.mark || i} className={p.id === activePanelId ? 'active-row' : ''}>
                <td>{r.mark}</td>
                <td>{fmtVol(r)}</td>
                <td>{fmtWt(r)}</td>
                <td>{fmtCg(r.cgX)}</td>
                <td>{fmtCg(r.cgY)}</td>
                <td>{fmtArea(r.finishedAreaSqFt, r.finishedAreaSqM)}</td>
                <td>{formatNumberOrDash(r.slendernessRatio, undefined, 1)}</td>
                <td>{fmtLoad(r.craneLoadPerPickLb)}</td>
                <td>{fmtLoad(r.braceLoadPerPointLb)}</td>
                <td>{formatNumberOrDash(r.braceCount)}</td>
                <td>{formatNumberOrDash(r.pickCount)}</td>
                <td>{formatNumberOrDash(r.weldCount)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
